import { meQueryOptions, useUserId } from "@/api/auth";
import { makeHomeQueryOptions } from "@/api/home";
import { infiniteSearchForListingsQueryOptions, LocationType } from "@/api/listingSearch";
import { countryCapabilitiesQuery } from "@/api/location";
import { favouritesQuery, myLocationQueryOptions, mySupportersQuery } from "@/api/me";
import { myPushDevicesQuery, NOTIFICATIONS_DENIED_KEY, pushSubscriptionQuery } from "@/api/myNotifications";
import { queryClient } from "@/api/utils";
import { FavouriteCard } from "@/components/FavouriteCard";
import { InfiniteScrollEnd } from "@/components/InfiniteScrollEnd";
import { ListingCard, ListingCardSkeleton } from "@/components/ListingCard";
import { PageInset } from "@/components/PageInset";
import { ButtonLink, FlexLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Shelf } from "@/components/ui/Shelf";
import { TopAppBar } from "@/components/ui/TopAppBar";
import { useIsStandalone, useRequiresInstall } from "@/hooks/installation";
import { useNotificationPermission } from "@/hooks/useServiceWorker";
import { AcceptNotificationsBox } from "@/pages/(app)/marketplace/AcceptNotificationsBox";
import { NoConnectionBox } from "@/pages/(app)/marketplace/NoConnectionBox";
import { captureCustomEvent } from "@/utils/posthog";
import { config } from "@common/utils/config";
import { formatWholeNumber } from "@common/utils/i18n";
import {
  Box,
  Flex,
  Heading,
  Img,
  InfiniteQueryHandler,
  QueryHandler,
  Skeleton,
  Span,
  Spinner,
  Text,
} from "@givenwell/components";
import { useIsOnline } from "@givenwell/hooks";
import {
  FavouriteListingMetadata,
  GetHomeControllerListingsResponseListing,
  GetHomeControllerListingsResponseParams,
  SearchForListingsRequest,
  SearchResult,
} from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, Link, stripSearchParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { array, fallback, finite, number, object, optional, pipe, string, toMinValue } from "valibot";
import { InstallPromptBox } from "./InstallPromptBox";
import { IntroCard } from "./IntroCard";
import { SearchPanel } from "./SearchPanel";
import confidentialC from "./confidential-C.png";
import { useSearchRadiusStore } from "./useSearchRadius";

/**
 * Stands in for "no maximum price" when the member hasn't set an upper bound.
 * The API's MaxPrice is an Int32, so Number.MAX_SAFE_INTEGER would fail to
 * deserialize server-side. We always send a price_range so the search keeps
 * hitting the filtered query path (which applies the valid_until and
 * search-radius filters) rather than the unfiltered fast path.
 */
const UNBOUNDED_MAX_PRICE = 2147483647;

export const Route = createFileRoute("/_auth/_app/marketplace/")({
  validateSearch: object({
    q: fallback(optional(string(), ""), ""),
    "location-type": fallback(optional(string(), ""), ""),
    "max-tokens": fallback(optional(pipe(number(), finite(), toMinValue(-1)), -1), -1),
    category: fallback(optional(array(string()), [] as string[]), [] as string[]),
    "variant-type": fallback(optional(array(string()), [] as string[]), [] as string[]),
    "available-from": fallback(optional(string()), undefined),
    "available-before": fallback(optional(string()), undefined),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        q: "",
        "location-type": "",
        "max-tokens": -1,
        category: [],
        "variant-type": [],
        "available-from": undefined,
        "available-before": undefined,
      }),
    ],
  },
  loader: () => {
    queryClient.ensureQueryData(mySupportersQuery());
    queryClient.prefetchQuery(makeHomeQueryOptions(40000, "marketplace"));
  },
  component: Home,
});

const route = getRouteApi("/_auth/_app/marketplace/");

// We need a unique ID for manual scroll restoration on a specific element
// It should be as unique as possible for this element across your app
const scrollRestorationId = "marketplace-scrollbox";
function Home() {
  // Redirect to claims if marketplace is not allowed.
  const location = useQuery(myLocationQueryOptions);
  const countryCapsQuery = useQuery(countryCapabilitiesQuery(location.data?.country_code));
  const navigate = route.useNavigate();
  useEffect(() => {
    if (countryCapsQuery.data) {
      const { marketplace_allowed } = countryCapsQuery.data;
      if (!marketplace_allowed) {
        navigate({ to: "/claims" });
      }
    }
  }, [countryCapsQuery.data, navigate]);

  const search = route.useSearch();

  const isSearching =
    !!search.q?.trim() ||
    !!search["location-type"]?.trim() ||
    (search.category && search.category.length > 0) ||
    (search["variant-type"] && search["variant-type"]?.length > 0) ||
    search["max-tokens"] !== -1 ||
    search["available-from"] ||
    search["available-before"];

  return (
    <PageWrapper topAppBar={<TopAppBar />}>
      <Flex
        css={{
          flexDirection: "column",
          width: "100%",
          height: "100%",
          overflowY: "auto",
        }}
        data-scroll-restoration-id={scrollRestorationId}
      >
        <PageInset>
          <Box
            css={{ width: "100%", maxW: 2000, mx: "auto" }}
            style={{
              maxWidth: 2000,
            }}
          >
            <Box css={{ pb: "var(--layout-margin)" }}>
              <IntroCard />
            </Box>
            <Box css={{ display: "flex", flexDir: "column", alignItems: "center" }}>
              <SearchPanel />
            </Box>
            {isSearching ? (
              <SearchResultsGrid />
            ) : (
              <>
                <HeroMessages />
                <RecommendedListingsFeed />
              </>
            )}
          </Box>
        </PageInset>
      </Flex>
    </PageWrapper>
  );
}

function RecommendedListingsFeed() {
  const searchRadius = useSearchRadiusStore(state => state.searchRadius);
  const listingGroupsQuery = useQuery(makeHomeQueryOptions(searchRadius, "marketplace"));
  const favourites = useQuery(favouritesQuery);

  const favouriteMarketplaceListings = favourites.data?.listings.filter(listing => !listing.eap_available) || [];

  return (
    <Box css={{ pt: 16 }}>
      <QueryHandler
        query={listingGroupsQuery}
        success={({ data }) => (
          <div
            style={{
              position: "relative",
              width: "100%",
            }}
          >
            {favourites.data && favouriteMarketplaceListings.length > 0 && (
              <ListingCardShelf
                groupName="Your Favourites"
                searchResults={favouriteMarketplaceListings}
                favouritesMetadata={favourites.data.listings_metadata}
              />
            )}
            {data.map(virtualItem => {
              const group = virtualItem;
              return (
                <ListingCardShelf
                  key={group.name}
                  groupName={group.name}
                  params={group.params}
                  searchResults={group.listings}
                />
              );
            })}
          </div>
        )}
        pending={() => (
          <>
            <ShelfSkeleton />
            <ShelfSkeleton />
            <ShelfSkeleton />
            <ShelfSkeleton />
            <ShelfSkeleton />
          </>
        )}
      />
    </Box>
  );
}

type ListingCardShelfProps = {
  groupName: string;
  params?: GetHomeControllerListingsResponseParams;
  searchResults: Array<SearchResult | GetHomeControllerListingsResponseListing>;
  favouritesMetadata?: FavouriteListingMetadata[];
};
function ListingCardShelf(props: ListingCardShelfProps) {
  const categoryIds = props.params?.category_ids ?? [];
  const variantTypes = props.params?.variant_types ?? [];
  const hasSearchParams = categoryIds.length > 0 || variantTypes.length > 0;

  const hasMore =
    props.params && (categoryIds.length > 0 || variantTypes.length > 0) && props.searchResults.length > 24;

  return (
    <Shelf
      title={props.groupName}
      enableSeeMore={hasMore}
      seeMoreLinkOptions={{
        to: "/marketplace",
        search: {
          category: hasSearchParams && categoryIds.length > 0 ? categoryIds : undefined,
          "variant-type": hasSearchParams && variantTypes.length > 0 ? variantTypes : undefined,
        },
      }}
      onSeeMore={() => {
        captureCustomEvent("member_filtered_by_theme", {
          theme_name: props.groupName,
        });
      }}
    >
      {props.searchResults
        .slice(0, 24)
        .map((listing, i) =>
          props.favouritesMetadata ? (
            <FavouriteCard
              key={i}
              listing={listing}
              metadata={props.favouritesMetadata.find(md => md.listing_id === listing.listing_id)}
            />
          ) : (
            <ListingCard key={i} listing={listing} />
          ),
        )}
    </Shelf>
  );
}

function ShelfSkeleton({ size }: { size?: "big" }) {
  return (
    <Shelf
      size={size}
      title={
        <Heading css={{ fontScale: "md", color: colors.blue800 }}>
          <Skeleton>Something</Skeleton>
        </Heading>
      }
    >
      {[1, 2, 3, 4, 5, 6].map(listing => (
        <ListingCardSkeleton key={listing} />
      ))}
    </Shelf>
  );
}

function SearchResultsGrid() {
  const {
    "variant-type": variantType,
    "max-tokens": maxTokens,
    "location-type": locationType,
    "available-from": availableFrom,
    "available-before": availableBefore,
    category,
    q,
  } = route.useSearch();

  const locationTypes = config.features.onlineListingsOnly
    ? ["online" as LocationType]
    : locationType
      ? [...(locationType.split(",") as LocationType[])]
      : [];

  const searchRadius = useSearchRadiusStore(state => state.searchRadius);
  const me = useQuery(meQueryOptions);

  const query = useInfiniteQuery(
    infiniteSearchForListingsQueryOptions({
      category_id: category || [],
      variant_types: (variantType as SearchForListingsRequest["variant_types"]) || [],
      location_types: locationTypes,
      price_range: {
        min_price: 0,
        max_price: maxTokens !== -1 ? maxTokens : UNBOUNDED_MAX_PRICE,
      },
      ...(availableFrom &&
        availableBefore && {
          availability_range: {
            start_utc: availableFrom,
            end_utc: availableBefore,
          },
          variant_types: ["booking"],
        }),
      listing_type: "marketplace",
      search_text: q || null,
      search_radius_meters: searchRadius,
    }),
  );

  return (
    <InfiniteQueryHandler
      query={query}
      success={({ data }) => {
        const isEmpty = data.pages.flatMap(p => p.items).length === 0;

        if (isEmpty) {
          return (
            <Flex
              css={{
                flexDirection: "column",
                py: 16,
                gap: 16,
                align: "center",
                width: "100%",
                maxW: 400,
                mx: "auto",
              }}
            >
              <Img src={confidentialC} alt="" css={{ width: "100%", maxW: 150, mx: "auto" }} />
              <Heading css={{ fontScale: "2xl", lineHeight: 1.1 }}>Manifest what you want</Heading>
              <Text css={{ fontScale: "sm" }}>
                We couldn't find what you're looking for. If you think we're missing something,{" "}
                <Link
                  to="/nominate"
                  search={{
                    from: location.pathname + location.search,
                    email: me.data?.email,
                  }}
                >
                  <Span
                    css={{
                      color: colors.blue500,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    let us know
                  </Span>
                  !
                </Link>
              </Text>
              <Text css={{ fontScale: "sm", mb: 16 }}>
                Alternatively, you can make a receipt claim to be reimbursed for something you've already bought.
              </Text>
              <ButtonLink to="/claims" variant="filled" size="lg">
                Make a receipt claim
              </ButtonLink>

              <ButtonLink to="/marketplace" variant="tonal" size="lg">
                Back to Marketplace
              </ButtonLink>
            </Flex>
          );
        }

        return (
          <>
            {data.pages[0] && (
              <Flex css={{ items: "center", py: 16 }}>
                <Text css={{ fontScale: "sm", weight: 500 }}>
                  {formatWholeNumber(data.pages[0].total_items)}{" "}
                  {data.pages[0].total_items === 1 ? "result" : "results"}
                </Text>
                <Box css={{ flex: "1 0 0px" }} />
                <FlexLink
                  css={{
                    h: 32,
                    px: 12,
                    fontScale: "xs",
                    weight: 500,
                    backgroundColor: colors.warmGrey,
                    items: "center",
                    borderRadius: 9999,
                  }}
                  to="/marketplace"
                >
                  Reset
                </FlexLink>
              </Flex>
            )}
            <ListingCardGrid>
              {data.pages
                .flatMap(p => p.items)
                .map((item, i) => (
                  <ListingCard listing={item} key={i} />
                ))}
              {query.hasNextPage && (
                <InfiniteScrollEnd
                  onReach={() => {
                    if (query.hasNextPage) {
                      query.fetchNextPage();
                    }
                  }}
                >
                  {query.isFetchingNextPage && <Spinner />}
                </InfiniteScrollEnd>
              )}
            </ListingCardGrid>
          </>
        );
      }}
      pending={() => (
        <>
          <Flex css={{ py: 16 }}>
            <Skeleton>123 results</Skeleton>
          </Flex>

          <ListingCardGrid>
            {Array.from({ length: 24 }).map((_, i) => (
              <ListingCardSkeleton key={i} />
            ))}
          </ListingCardGrid>
        </>
      )}
    />
  );
}

const ListingCardGrid = styled("div", {
  display: "grid",
  gridCols: 2,
  pb: 16,
  columnGap: 8,
  rowGap: 32,
  "@sm": {
    gridCols: 3,
  },
  "@md": {
    columnGap: 16,
    gridCols: 4,
  },
  "@lg": {
    gridCols: 4,
  },
  "@xl": {
    gridCols: 6,
  },
});

function usePromptForNotifications() {
  const isInstalledInStandalone = useIsStandalone();
  const userId = useUserId();
  const myDevices = useQuery({ ...myPushDevicesQuery(userId), enabled: !!userId });
  const permission = useNotificationPermission();
  const subscription = useQuery(pushSubscriptionQuery);

  const subscriptionIsRegistered = myDevices.data?.includes(subscription.data?.endpoint || "");
  const isAlreadyRegistered = permission === "granted" && subscriptionIsRegistered;
  const hasDeniedNotifications = permission === "denied" || localStorage[NOTIFICATIONS_DENIED_KEY];

  return (
    myDevices.isSuccess &&
    subscription.isSuccess &&
    isInstalledInStandalone &&
    !isAlreadyRegistered &&
    !hasDeniedNotifications
  );
}

function HeroMessages() {
  const isOffline = !useIsOnline();
  const requiresInstall = useRequiresInstall();
  const promptForNotifications = usePromptForNotifications();

  return (
    <>
      {isOffline ? (
        <NoConnectionBox />
      ) : requiresInstall ? (
        <InstallPromptBox />
      ) : promptForNotifications ? (
        <AcceptNotificationsBox />
      ) : null}
    </>
  );
}
